import React from 'react'
import { IconPiggyBank } from '@posthog/icons'
import OSButton from 'components/OSButton'
import Link from 'components/Link'

export const posthog = {
    name: 'PostHog',
    key: 'posthog',
    assets: {
        icon: '/images/logo.svg',
    },
    products: {
        heatmaps: {
            available: true,
            features: {
                clickmaps: true,
                dead_taps: false,
                heatmaps: true,
                scrollmaps: true,
                movement_maps: true,
                rage_clicks: true,
                rage_taps: true,
                save_heatmaps: false,
                toolbar: true,
            },
        },
        error_tracking: {
            available: true,
            pricing: {
                free_tier: '100k',
            },
            features: {
                code_context: true,
                console_log_capture: true,
                error_alerts: true,
                error_grouping: true,
                exception_capture: true,
                issue_management: true,
                log_management: 'Beta',
                mobile_sdk_coverage: true,
                profiling: false,
                source_map_support: true,
                stack_tracing: false,
                user_device_context: true,
            },
            monitoring: {
                features: {
                    cron_monitoring: false,
                    distributed_tracing: false,
                    release_tracking: true,
                    performance_monitoring: true,
                },
            },
            integrations: {
                datadog: false,
                product_analytics: true,
                session_replay: true,
                ab_experiments: true,
            },
        },
        llm_analytics: {
            available: true,
        },
        session_replay: {
            available: true,
            beta: false,
            pricing: {
                free_tier: '5,000 web recordings, 2,500 mobile recordings',
            },
            features: {
                canvas_recording: true,
                chat_with_recordings: true,
                conditional_recording: true,
                crash_reports: false,
                event_timeline: true,
                filter_by_user_or_event: true,
                highlights: 'Beta',
                identity_detection: true,
                iframe_recording: true,
                minimum_duration: true,
                movement_maps: false,
                notes_on_replays: true,
                playlists: true,
                privacy_masking: true,
                retention_policy: 'Up to 3 months',
                sentiment_scores: false,
                screenshot_mode: true,
                scrollmaps: true,
                search_by_network: false,
                share_replays: true,
                single_page_app: true,
                target_by_feature_flag: true,
                target_by_sample: true,
                target_by_url: true,
                wireframe_mode: true,
            },
            export: {
                features: {
                    export_to_json: true,
                    export_to_video: 'Beta',
                    retention_policy: 'Up to 3 months',
                },
            },
            platform_support: {
                features: {
                    web_app_recordings: true,
                    mobile_app_recordings: true,
                    ios_recordings: true,
                    android_recordings: true,
                    react_native_recordings: true,
                    flutter_recordings: true,
                },
            },
            analysis: {
                features: {
                    console_logs: true,
                    performance_monitoring: true,
                    network_monitor: true,
                    dom_explorer: true,
                },
            },
            ai: {
                features: {
                    ai_summaries: 'Beta',
                },
            },
        },
        product_analytics: {
            available: true,
            pricing: {
                free_tier: '1 million events',
            },
            features: {
                actions: true,
                advertising_analytics: 'Beta',
                ai_analysis: false,
                alerts: true,
                anomaly_detection: true,
                any_step_order: true,
                autocapture: true,
                breakdown_by_person_property: true,
                cohorts: true,
                conversion_funnels: true,
                conversion_windows: true,
                correlation_analysis: true,
                custom_events: true,
                custom_properties: true,
                define_number_of_users_on_path: true,
                exclusion_events: true,
                filter_by_cohort: true,
                filter_by_events_or_person_property: true,
                filter_by_person_property: true,
                filter_internal_and_test_users: true,
                hide_repeating_steps: true,
                historical_trends: true,
                include_and_exclude_wildcards: true,
                max_number_of_steps: '20',
                monetization_analytics: (
                    <>
                        Via{' '}
                        <Link to="/revenue-analytics" className="group !no-underline" state={{ newWindow: true }}>
                            <IconPiggyBank className="size-4 inline-block text-green" />{' '}
                            <span className="group-hover:!underline">Revenue Analytics</span>
                        </Link>
                    </>
                ),
                predictive_insights: false,
                private_insights: true,
                project_level_permissions: true,
                ready_made_insight_types: true,
                real_time_view: true,
                regex_for_path_cleaning: true,
                sequential_step_order: true,
                strict_step_order: true,
                time_to_convert_insights: true,
                toolbar: true,
                track_custom_events: true,
                track_pageviews: true,
                user_level_permissions: true,
                user_profiles: true,
                trends: {
                    available: true,
                    features: {},
                },
                funnels: {
                    available: true,
                    features: {
                        conversion_funnels: true,
                        conversion_windows: true,
                        correlation_analysis: true,
                        exclusion_events: true,
                        filter_by_cohort: true,
                        filter_by_events_or_person_property: true,
                        filter_by_person_property: true,
                        filter_internal_and_test_users: true,
                        hide_repeating_steps: true,
                        historical_trends: true,
                        max_number_of_steps: '20',
                        private_insights: true,
                        project_level_permissions: true,
                    },
                },
                user_paths: {
                    available: true,
                    features: {
                        reveal_paths_between_points: true,
                        reveal_paths_from_a_start_point: true,
                        reveal_paths_from_an_end_point: true,
                        reveal_paths_within_funnels: true,
                        reveal_user_paths_between_steps: true,
                    },
                },
                group_analytics: {
                    available: true,
                    features: {
                        group_by_events: true,
                    },
                },
                insights: {
                    available: true,
                    features: {
                        ai_insight_builder: true,
                        alerts: true,
                        formula_mode: true,
                        ready_made_insight_types: true,
                        sampling: true,
                        sql_editor: true,
                    },
                },
            },
        },
        dashboards: {
            available: true,
            features: {
                annotations: true,
                custom_dashboards: true,
                dashboard_level_permissions: true,
                dashboard_tags: true,
                embed_dashboards: false,
                pinned_dashboards: true,
                private_insights: true,
                project_level_permissions: true,
                share_dashboards_externally: true,
                subscribe_to_dashboards: true,
                user_level_permissions: true,
            },
        },
        product_tours: {
            available: false,
        },
        feature_flags: {
            available: true,
            pricing: {
                free_tier: '1 million API requests',
            },
            features: {
                automation: true,
                boolean_flags: true,
                early_access_management: true,
                multivariate_flags: true,
                json_payloads: true,
                release_conditions: true,
                remote_config: true,
                single_page_app_support: true,
                usage_logs: true,
            },
            implementation: {
                features: {
                    local_evaluation: true,
                    bootstrapping: true,
                    api_access: true,
                    sdk_support: true,
                },
            },
            targeting: {
                features: {
                    target_by_percentage: true,
                    target_by_person_properties: true,
                    target_by_cohorts: true,
                    geographic_targeting: true,
                    group_targeting: true,
                },
            },
            management: {
                features: {
                    approvals: true,
                    data_source: 'First-party',
                    flag_administration: true,
                    flag_scheduling: true,
                    history_activity: true,
                    instant_rollbacks: true,
                    lifecycle: true,
                    multi_environment: 'Partial',
                    permissioning: true,
                    triggers: false,
                },
            },
            testing: {
                features: {
                    flag_overrides: true,
                    toolbar_integration: true,
                    user_assignment: true,
                },
            },
            experimentation: {
                features: {
                    experimentation: true,
                    correlation_analysis: true,
                },
            },
            advanced: {
                features: {
                    persist_across_auth: true,
                },
            },
        },
        experiments: {
            available: true,
            pricing: {
                free_tier: '1 million API requests',
            },
            features: {
                count_value_metrics: true,
                custom_goals: true,
                dynamic_cohorts: true,
                experiment_analysis: true,
                funnel_metrics: true,
                funnel_tests: true,
                namespacing: false,
                native_goal_tracking: true,
                no_code_experiments: 'Beta',
                low_code_experiments: true,
                ratio_metrics: true,
                recommended_run_time: true,
                secondary_metrics: true,
                shared_metrics_library: true,
                split_testing: true,
                visual_editor: 'Beta',
            },
            supported_tests: {
                features: {
                    aa_testing: true,
                    ab_testing: true,
                    abn_testing: true,
                    data_warehouse_experiments: 'Beta',
                    fake_door_testing: true,
                    holdout_testing: 'Partial',
                    multi_armed_bandit: false,
                    mutually_exclusive_experiments: false,
                    redirect_testing: true,
                },
            },
            targeting: {
                features: {
                    cohort_integration: true,
                    custom_targeting: true,
                    exclusion_rules: true,
                    geographic_targeting: true,
                    group_level_experiments: true,
                    holdouts: true,
                },
            },
            implementation: {
                features: {
                    api_access: true,
                    feature_flag_foundation: true,
                    json_payloads: true,
                    multivariate_testing: true,
                },
            },
            analysis: {
                features: {
                    llm_support: true,
                    results_visualization: true,
                    side_effect_monitoring: true,
                    statistical_significance: true,
                    statistics_engine: 'Bayesian or Sequential',
                },
            },
            platforms: {
                features: {
                    mobile: true,
                    web: true,
                },
            },
        },
        surveys: {
            available: true,
            pricing: {
                free_tier: '1,500 responses',
            },
            features: {
                aggregated_results: true,
                ai_response_summaries: 'Beta',
                api_access: true,
                automatic_nps_calculation: true,
                calendar_invites: true,
                capture_partial_responses: true,
                cdp_destinations: true,
                completion_conditions: true,
                conditional_logic: true,
                csat_surveys: true,
                custom_html: true,
                custom_targeting: true,
                custom_ui: true,
                customizable_wait_periods: true,
                embedded_links: true,
                emoji_reaction: true,
                event_triggered: true,
                in_app_prompts_messages: true,
                feedback_button: true,
                freeform_text: true,
                hosted_surveys: true,
                iframe_embedding: true,
                integrations: true,
                link_to_webpage: true,
                multi_select: true,
                multiple_choice: true,
                no_code: true,
                nps_surveys: true,
                pmf_surveys: true,
                popover: true,
                rating: true,
                sdk_support: true,
                sentiment_analysis: false,
                slack_integration: true,
                survey_templates: true,
                target_by_feature_flag: true,
                user_interview_requests: true,
                webhooks: true,
            },
            presentation: {
                features: {
                    hosted_surveys: 'Beta',
                    custom_ui: true,
                },
            },
            targeting: {
                features: {
                    display_conditions: true,
                    event_triggered: true,
                    linked_feature_flag: true,
                },
            },
            question_types: {
                features: {
                    multiple_choice: true,
                    multi_select: true,
                    rating: true,
                    emoji_reaction: true,
                    interview_scheduling: true,
                },
            },
            branching: {
                features: {
                    multi_step_surveys: true,
                },
            },
            platforms: {
                features: {
                    web: true,
                    mobile: true,
                },
            },
        },
        revenue_analytics: {
            available: true,
            beta: true,
            features: {
                revenue_tracking: true,
                event_based_revenue_tracking: true,
                native_multi_source_revenue_tracking: true,
                deferred_revenue: true,
                product_analytics_integration: true,
                multi_currency_support: true,
                revenue_prediction: false,
                sql_access: true,
            },
        },
        cdp: {
            available: true,
            features: {
                number_of_integrations: '60+',
                realtime_streaming: true,
                custom_transformations: true,
                no_code_setup: true,
                built_in_analytics: true,
            },
        },
        llm_analytics: {
            available: true,
            features: {
                alerting: true,
                cost_tracking: true,
                generation_tracking: true,
                latency_tracking: true,
                prompt_evaluations: false,
                prompt_playground: true,
                token_tracking: true,
                trace_visualization: true,
            },
        },
        data_warehouse: {
            available: true,
            pricing: {
                free_tier: '1 million synced rows',
            },
            features: {
                sql_queries: true,
                save_queries_as_views: true,
                ai_sql: true,
                simplified_syntax: true,
                smart_joins: true,
                product_functions: true,
                warehouse_sources: true,
                unified_queries: true,
                custom_views: true,
                scheduled_syncs: true,
                cloud_storage: true,
                batch_exports: true,
            },
        },
        web_analytics: {
            available: true,
            features: {
                advertising_analytics: false,
                bounce_rate: true,
                bounce_rate_tracking: false,
                breakdown_by_device_and_browser: false,
                breakdown_by_geoip: false,
                conversions: true,
                cookieless_tracking: true,
                custom_channel_types: true,
                entry_exit_paths: true,
                first_party_cookies: false,
                hipaa_compliance: false,
                integration_with_feature_flags: true,
                integration_with_session_replay: true,
                integration_with_surveys: true,
                migration: true,
                open_source: false,
                outbound_clicks: true,
                pageviews: true,
                pre_configured_dashboards: false,
                real_time_reporting: false,
                revenue_tracking: false,
                script_size: '56.8 kb',
                search_tools: false,
                session_and_duration_tracking: false,
                sessions: true,
                snippet_install: true,
                traffic_breakdown: true,
                utm_tracking: true,
                visitor_and_view_tracking: false,
                web_vitals: true,
                web_vitals_reporting: false,
            },
        },
    },
    platform: {
        deployment: {
            eu_hosting: true,
            open_source: true,
            reverse_proxy: true,
            managed_reverse_proxy: true,
            self_host: true,
        },
        libraries: {
            features: {
                android: true,
                capacitor: true,
                dotnet: true,
                elixir: true,
                flutter: true,
                go: true,
                ios: true,
                java: true,
                javascript: true,
                node: true,
                php: true,
                python: true,
                react_native: true,
                react: true,
                ruby: true,
                rust: true,
            },
        },
        pricing: {
            usage_based_pricing: true,
            transparent_pricing: true,
            free_tier: true,
            self_serve: true,
        },
        integrations: {
            airbyte: true,
            azure_blob: true,
            bigquery: true,
            ci_cd_integrations: true,
            community_integrations: true,
            csv_exports: true,
            customer_io: true,
            datadog: false,
            discord: true,
            email_reports: true,
            exports: true,
            gcs: true,
            google_ads: true,
            google_analytics: false,
            google_search_console: false,
            hubspot: true,
            imports: true,
            intercom: true,
            microsoft_teams: true,
            postgres: true,
            project_management: true,
            redshift: true,
            rudderstack: true,
            s3: true,
            salesforce: true,
            segment: true,
            sentry: true,
            shopify: true,
            slack: true,
            snowflake: true,
            stripe: true,
            warehouse_import: true,
            wordpress: false,
            zapier: true,
            zendesk: true,
        },
        developer: {
            api: true,
            client_side_sdks: true,
            collaboration: 'Invite teammates to collaborate on all features',
            cross_domain_tracking: true,
            mobile_sdks: true,
            native_data_sources: true,
            sdks: '14',
            server_side_sdks: true,
            sql: true,
            tv_sdks: false,
        },
        tools: {
            cms: false,
            notebooks: true,
            project_management_tools: false,
        },
        security: {
            bot_blocking: true,
            ccpa_ready: true,
            cookieless_tracking: true,
            data_anonymization: true,
            data_retention: '7 years',
            dpa: true,
            gdpr_ready: true,
            hipaa_ready: true,
            history_audit_logs: 'Enterprise',
            role_based_access_control: true,
            saml_sso: 'Enterprise',
            soc2_certified: true,
            security_certification: 'SOC 2 Type II',
            two_factor_auth: true,
            user_privacy_options: true,
        },
    },
    pricing: {
        model: 'Usage-based',
    },
}
